import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import moment from 'moment'
import { useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import subscribersApi from 'services/api/subscribers'
import { emailRegex, formatPhoneNumber } from 'utils/helpers'

import { ReactComponent as MessageIcon } from './message.svg'
import MessagingSetupDialog from './MessagingSetupDialog'
import styles from './SearchGuestDialog.module.scss'

const unknownDate = '1978-09-12T00:00:00'

const GuestCard = ({
  fullName,
  state,
  city,
  lastCheckOut,
  phone,
  email,
  id,
  onClick
}) => {
  return (
    <div className={styles.guestCard} onClick={() => onClick(id)}>
      <div className={styles.header}>
        <p className={styles.guestName}>{fullName}</p>
        <button>
          <MessageIcon />
          Message
        </button>
      </div>
      <div className={styles.guestRow}>
        <p className={styles.guestLabel}>From</p>
        <p className={styles.guestValue}>
          {city}
          {city && state && ','} {state}
        </p>
      </div>
      <div className={styles.guestRow}>
        <p className={styles.guestLabel}>Phone</p>
        <p className={styles.guestValue}>{formatPhoneNumber(phone)}</p>
      </div>
      <div className={styles.guestRow}>
        <p className={styles.guestLabel}>Email</p>
        <p className={styles.guestValue}>{email}</p>
      </div>
      <div className={styles.guestRow}>
        <p className={styles.guestLabel}>Last checkout</p>
        <p className={styles.guestValue}>
          {lastCheckOut === unknownDate
            ? 'Unknown'
            : moment(lastCheckOut).format('MMM DD, YYYY')}
        </p>
      </div>
    </div>
  )
}

const SearchGuestDialog = ({
  resultsTitle,
  onHideDialog,
  onSelectGuest,
  onStartChat
}) => {
  const [isSearching, setIsSearching] = useState(false)
  const [showNoResultsMessage, setShowNoResultsMessage] = useState(false)

  const [results, setResults] = useState()

  const [type, setType] = useState('name')

  const [phone, setPhone] = useState('')

  const { addToast } = useToasts()

  function handlePhoneInputChange(e) {
    setPhone(formatPhoneNumber(e.target.value))
  }

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const [email, setEmail] = useState('')

  function handleSearch() {
    let findParams

    if (type === 'name') {
      findParams = {
        firstName,
        lastName
      }
    }

    if (type === 'email') {
      findParams = {
        email
      }
    }

    if (type === 'phone') {
      findParams = {
        phone: 1 + phone.replace(/[^0-9]/g, '')
      }
    }

    setIsSearching(true)
    setShowNoResultsMessage(false)

    subscribersApi
      .find(findParams)
      .then(res => {
        const results = res?.subscribersFound

        if (Object.keys(results).length === 0) {
          setShowNoResultsMessage(true)
        } else {
          setResults(results)
        }
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsSearching(false)
      })
  }

  function getIsButtonDisabled() {
    if (type === 'name') {
      return !(firstName.length > 1 && lastName.length > 1)
    }

    if (type === 'email') {
      return !email.match(emailRegex)
    }

    if (type === 'phone') {
      return phone.length < 14
    }
  }

  if (!results) {
    return (
      <ConfirmationDialog
        title="Find a guest by"
        positiveMessage="Search"
        onConfirm={handleSearch}
        onPressEnter={handleSearch}
        onClose={onHideDialog}
        isButtonDisabled={getIsButtonDisabled()}
      >
        {isSearching && <LoadingIndicator />}
        <div className={isSearching ? styles.formSearching : styles.form}>
          <div className={styles.radioHolder}>
            <span className={styles.searchBy}>By: </span>
            <label>
              <input
                type="radio"
                name="type"
                value="name"
                checked={type === 'name'}
                onChange={() => setType('name')}
              />
              <span className={styles.label}>Full name</span>
            </label>
            <label>
              <input
                type="radio"
                name="type"
                value="phone"
                checked={type === 'phone'}
                onChange={() => setType('phone')}
              />
              <span className={styles.label}>Phone</span>
            </label>
            <label>
              <input
                type="radio"
                name="type"
                value="email"
                checked={type === 'email'}
                onChange={() => setType('email')}
              />
              <span className={styles.label}>Email</span>
            </label>
          </div>
          {type === 'phone' && (
            <TextInput
              label="Phone number"
              type="text"
              className={styles.input}
              onChange={handlePhoneInputChange}
              value={phone}
            />
          )}
          {type === 'name' && (
            <div className={styles.nameInputs}>
              <TextInput
                label="First name"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
              />
              <TextInput
                label="Last name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
              />
            </div>
          )}
          {type === 'email' && (
            <TextInput
              label="Email"
              type="email"
              className={styles.input}
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          )}
        </div>
        {showNoResultsMessage && (
          <MessagingSetupDialog
            phoneNumber={`+1${phone}`}
            guestFirstName={firstName}
            guestLastName={lastName}
            handleCancel={onHideDialog}
            handleConfirm={onStartChat}
          />
        )}
      </ConfirmationDialog>
    )
  }

  if (results) {
    return (
      <ConfirmationDialog
        title={resultsTitle || 'Search results'}
        positiveMessage="Search"
        onClose={onHideDialog}
        hasConfirmButton={false}
        negativeMessage="Back to search"
        onReject={() => setResults(null)}
        theme="blacklist"
      >
        <div>
          {Object.values(results).map(r => (
            <GuestCard {...r} key={r.id} onClick={onSelectGuest} />
          ))}
        </div>
      </ConfirmationDialog>
    )
  }
}

export default SearchGuestDialog
